import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import CheckInFormContent from '../StatusCheckInDialog/CheckInFormContent';
import type { StatusValue } from '../StatusCheckInDialog/CheckInFormContent';
import SphereIdentityAtom from '../../atoms/sphere-identity/SphereIdentityAtom';
import UrgencyIcon from '../../atoms/urgency/urgency';
import VelocityIndicator from '../../atoms/VelocityIndicator/VelocityIndicator';
import DateDisplay from '../../atoms/date-display/date-display';
import StatusBadgeAtom from '../../atoms/status-badge/StatusBadgeAtom';
import type { StatusValue as CanonicalStatusValue } from '../../atoms/status-badge/StatusBadgeAtom';
import { Task } from '../TaskList/TaskListMolecule.types';
import './TaskCardMolecule.scss';

/** Prefix added by the API when a check-in description is auto-generated from the factor name */
const CHECK_IN_DESCRIPTION_PREFIX = 'Check-in for ';

/** Canonical six-value status set — values outside this set render as unknown via StatusBadgeAtom */
const CANONICAL_STATUS_SET = new Set<string>(['on_track', 'at_risk', 'needs_attention', 'critical', 'blocked', 'unknown']);

export interface TaskCardMoleculeProps {
  /** Task data to display */
  task: Task;
  /** Graph sphere ID of the current user (for assignee comparison) */
  currentUserId?: string;
  /** Display name of the current user */
  currentUserName?: string;
  /** Callback when a workflow task's edit sphere action is triggered */
  onEditSphere?: (taskId: string, task: Task) => void;
  /** Callback when the card is clicked */
  onClick?: (task: Task) => void;
  /** Callback when check-in action is triggered */
  onCheckIn?: (task: Task) => void;
  /** Whether the inline check-in form is expanded */
  isCheckInExpanded?: boolean;
  /** Callback when expansion state changes */
  onCheckInExpandChange?: (taskId: string, expanded: boolean) => void;
  /** Whether the check-in was just submitted successfully (triggers flash) */
  checkInSuccess?: boolean;
  /** Callback when inline check-in form is submitted */
  onCheckInSubmit?: (statusValue: StatusValue, comment?: string) => void;
  /** Whether a check-in submission is in progress */
  isCheckInSubmitting?: boolean;
  /** Error message from check-in submission */
  checkInError?: string | null;
  /** Whether this card is currently active/selected */
  isActive?: boolean;
  /** Additional CSS class names */
  className?: string;
}

/**
 * Get factor name from task, stripping "Check-in for " prefix if present
 */
export function getFactorName(task: Task): string {
  if (task.factorName) return task.factorName;
  if (task.description?.startsWith(CHECK_IN_DESCRIPTION_PREFIX)) {
    return task.description.slice(CHECK_IN_DESCRIPTION_PREFIX.length);
  }
  return task.description || task.name;
}

/** Maps urgency level (1-5) to CSS custom property for border color */
const URGENCY_BORDER_MAP: Record<number, string> = {
  5: 'var(--urgency-5)',
  4: 'var(--urgency-4)',
  3: 'var(--urgency-3)',
  2: 'var(--urgency-2)',
  1: 'var(--urgency-1)',
};

/**
 * Get left border CSS variable color by task status and type.
 * Status overrides take priority; workflow tasks use urgency-based colors;
 * check-in tasks get green (actionable).
 */
export function getBorderColor(task: Task): string {
  // Status overrides (highest priority)
  if (task.status === 'overdue') return 'var(--danger-500)';
  if (task.status === 'complete') return 'var(--success-500)';
  if (task.status === 'skipped') return 'var(--surface-400)';
  // Check-in tasks: green = actionable
  if (task.taskType === 'check_in') return 'var(--success-500)';
  // Workflow tasks: urgency determines border color
  return URGENCY_BORDER_MAP[task.urgency ?? 3] ?? 'var(--urgency-3)';
}

/**
 * Get BEM status modifier class name from task status.
 * Workflow tasks in pending/in_progress use urgency-based borders instead.
 */
function getStatusModifier(task: Task): string {
  if (task.status === 'overdue') return 'task-card-molecule--overdue';
  if (task.status === 'complete') return 'task-card-molecule--complete';
  if (task.status === 'skipped') return 'task-card-molecule--skipped';
  // Workflow cards use urgency border for pending/in_progress — skip status modifier
  if (task.taskType === 'workflow') return '';
  if (task.status === 'in_progress') return 'task-card-molecule--in-progress';
  return 'task-card-molecule--pending';
}


/**
 * TaskCardMolecule - Individual task card with three distinct layouts:
 * workflow, check-in (single), and check-in (collapsed group).
 * Status is conveyed via left border only — no status dots or badges.
 */
const TaskCardMolecule: React.FC<TaskCardMoleculeProps> = ({
  task,
  currentUserId,
  currentUserName: _currentUserName,
  onEditSphere,
  onClick,
  onCheckIn,
  isCheckInExpanded = false,
  onCheckInExpandChange,
  checkInSuccess = false,
  onCheckInSubmit,
  isCheckInSubmitting = false,
  checkInError,
  isActive = false,
  className = '',
}) => {
  const { t, i18n } = useTranslation();

  // Card type detection
  const isWorkflow = task.taskType === 'workflow';
  const isCollapsed = (task.groupCount ?? 0) > 1;
  const isCheckIn = !isWorkflow && !isCollapsed;

  const showAssignee = !!(task.assignee && (!currentUserId || task.assignee.id !== currentUserId));
  const isOverdue = task.status === 'overdue';
  const isComplete = task.status === 'complete';
  const isSkipped = task.status === 'skipped';
  const isCheckInTask = task.taskType === 'check_in';
  const showActionButton = isCheckInTask && !isComplete && !isSkipped && !task.isVirtual && !isCheckInExpanded;
  const showWorkflowAction = isWorkflow && !isComplete && !isSkipped && !!onEditSphere;
  const useInlineExpansion = !!onCheckInExpandChange;

  const [showAbsoluteDate, setShowAbsoluteDate] = useState(false);

  // --- Event handlers ---

  const handleCardClick = () => {
    onClick?.(task);
  };

  const handleCheckIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCollapsed && task.requireAllPeriods) {
      onClick?.(task);
      return;
    }
    if (useInlineExpansion) {
      onCheckInExpandChange(task.id, !isCheckInExpanded);
    } else {
      onCheckIn?.(task);
    }
  };

  const handleEditSphere = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEditSphere?.(task.id, task);
  };

  const handleCheckInCancel = () => {
    onCheckInExpandChange?.(task.id, false);
  };

  const handleCheckInSubmit = (statusValue: StatusValue, comment?: string) => {
    onCheckInSubmit?.(statusValue, comment);
  };

  // --- Type-conditional title ---

  let primaryTitle: string;
  if (isWorkflow) {
    primaryTitle = task.description || task.name;
  } else {
    primaryTitle = getFactorName(task);
  }

  // --- Status chip (check-in single only) ---

  const canonicalStatusValue: CanonicalStatusValue | null = task.currentStatusValue && CANONICAL_STATUS_SET.has(task.currentStatusValue)
    ? task.currentStatusValue as CanonicalStatusValue
    : null;

  // --- Collapsed group date ---

  const collapsedSinceDate = isCollapsed && task.groupOldestDueAt
    ? new Date(task.groupOldestDueAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  // --- Type-conditional detail row (Zone 2) ---

  const renderDetailRow = () => {
    if (isCollapsed && collapsedSinceDate) {
      return (
        <div className="task-card-detail-row">
          {t('taskCard.periods.since', { date: collapsedSinceDate })}
        </div>
      );
    }

    if (isCheckIn) {
      return (
        <div className="task-card-detail-row">
          <span className="task-card-detail-text">{primaryTitle}</span>
          {task.velocityBucket && (
            <VelocityIndicator score={task.velocityScore ?? 0} bucket={task.velocityBucket} size="small" />
          )}
          {canonicalStatusValue && (
            <StatusBadgeAtom status={canonicalStatusValue} variant="chip" />
          )}
        </div>
      );
    }

    // Workflow detail: task description + arc stage + urgency shield icon
    return (
      <div className="task-card-detail-row">
        <span className="task-card-detail-text">{primaryTitle}</span>
        {task.arcStage && (
          <span className={`task-card__stage-chip task-card__stage-chip--${task.arcStage}`}>
            {t(`arc.stage.task.${task.arcStage}`)}
          </span>
        )}
        <UrgencyIcon urgencyValue={task.urgency ?? 3} width={16} height={16} />
      </div>
    );
  };

  // --- Card header (Zone 1: SphereIdentityAtom + date badge) ---

  const cardHeader = (
    <div className="task-card-header">
      <SphereIdentityAtom
        sphereId={task.sphereId || task.id}
        sphereName={task.sphereName || task.name}
        imageId={task.imageId}
        glyph={task.glyph}
        glyphColor={task.glyphColor}
        size="small"
        showInitials={!task.glyph}
        className="task-card-identity"
      />
      {task.dueAt && (
        <button
          className={`task-card__date-badge${isOverdue ? ' task-card__date-badge--overdue' : ''}`}
          onClick={(e) => { e.stopPropagation(); setShowAbsoluteDate(!showAbsoluteDate); }}
          type="button"
          aria-label={t('taskCard.toggleDateFormat')}
        >
          <DateDisplay date={new Date(task.dueAt)} dateFormat={i18n.language} isFriendly={!showAbsoluteDate} />
        </button>
      )}
    </div>
  );

  // --- BEM class composition ---

  const typeModifier = (() => {
    if (isWorkflow) return 'task-card-molecule--workflow';
    if (isCollapsed) return 'task-card-molecule--collapsed';
    return 'task-card-molecule--checkin';
  })();

  // Urgency BEM modifier for workflow cards (e.g. task-card-molecule--urgency-5)
  const urgencyModifier = isWorkflow
    ? `task-card-molecule--urgency-${task.urgency ?? 3}`
    : '';

  const cardClassName = [
    'task-card-molecule',
    typeModifier,
    getStatusModifier(task),
    urgencyModifier,
    task.isVirtual ? 'task-card-molecule--virtual' : '',
    isActive ? 'task-card-molecule--active' : '',
    checkInSuccess ? 'task-card--check-in-success' : '',
    className,
  ].filter(Boolean).join(' ');

  // --- Action button ---

  let actionButtonLabel: string;
  let actionButtonIcon: string;
  let actionButtonSeverity: 'secondary' | undefined;

  if (isCollapsed && task.requireAllPeriods) {
    actionButtonLabel = t('taskCard.periods.showAll');
    actionButtonIcon = 'pi pi-list';
    actionButtonSeverity = 'secondary';
  } else {
    actionButtonLabel = t('taskCard.checkIn');
    actionButtonIcon = 'pi pi-chart-line';
    actionButtonSeverity = undefined;
  }

  // --- Footer ---

  const hasFooterContent = isCollapsed || showAssignee || showActionButton || showWorkflowAction;

  return (
    <Card
      className={cardClassName}
      onClick={handleCardClick}
      header={cardHeader}
    >
      {renderDetailRow()}
      {hasFooterContent && (
        <div className="task-card-footer">
          <div className="task-card-meta">
            {isCollapsed && (
              <span className="task-card__period-count">
                {t('taskCard.periods.overduePending', { count: task.groupCount as number })}
              </span>
            )}
            {showAssignee && (
              <span className="task-card-assignee">
                <i className="pi pi-user" />
                <span className="task-card-assignee-text">
                  {t('taskCard.assignedTo', { name: task.assignee?.name })}
                  {task.viaRoleName && (
                    <> {t('taskCard.viaRole', { role: task.viaRoleName })}</>
                  )}
                </span>
              </span>
            )}
          </div>

          {showActionButton && (
            <div className="task-card-actions">
              <Button
                label={actionButtonLabel}
                icon={actionButtonIcon}
                severity={actionButtonSeverity}
                onClick={handleCheckIn}
                className="task-card__action-btn--full"
              />
            </div>
          )}

          {showWorkflowAction && (
            <div className="task-card-actions">
              <Button
                label={t('taskList.actions.update')}
                icon="pi pi-pencil"
                severity="secondary"
                onClick={handleEditSphere}
                className="task-card__action-btn--full"
              />
            </div>
          )}
        </div>
      )}

      {isCheckInTask && useInlineExpansion && (
        <div className={`task-card__checkin-container ${isCheckInExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="task-card__checkin-inner">
            <CheckInFormContent
              commentRequired={task.commentRequired}
              minCommentLength={task.minCommentLength}
              velocityScore={task.velocityScore}
              velocityBucket={task.velocityBucket}
              defaultStatus={task.currentStatusValue as StatusValue}
              isSubmitting={isCheckInSubmitting}
              error={checkInError}
              onSubmit={handleCheckInSubmit}
              onCancel={handleCheckInCancel}
            />
          </div>
        </div>
      )}
    </Card>
  );
};

export default TaskCardMolecule;
